import { useState, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError, api } from "../lib/api";
import { Button } from "../components/ui/button";
import Captcha from "../components/Captcha";
import { LogIn, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const captchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "customer" ? "/my-tickets" : "/workspace"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const captcha_token = await captchaRef.current?.getValue();
      // Empty token is OK — the backend bypasses captcha on allowlisted hosts
      // (e.g. preview domains that aren't registered on the reCAPTCHA key).
      const { data } = await api.post("/auth/login", {
        email: email.trim(),
        password,
        captcha_token: captcha_token || "",
      });
      localStorage.setItem("beacon_token", data.token);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
      window.location.href = data.user.role === "customer" ? "/my-tickets" : "/workspace";
    } catch (err) {
      setError(formatApiError(err));
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-14">
      <div className="beacon-card p-7 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Sign in</div>
            <div className="text-xs text-slate-500">Customers, agents and admins — same door.</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="beacon-label">Email</label>
            <input required type="email" className="beacon-input" data-testid="agent-login-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <div>
            <label className="beacon-label">Password</label>
            <input required type="password" className="beacon-input" data-testid="agent-login-password-input"
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <Captcha ref={captchaRef} action="login" testid="login-captcha" />
          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting} data-testid="agent-login-submit-btn"
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
            <LogIn className="w-4 h-4 mr-2" />
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <div className="mt-5 text-xs text-slate-500 text-center">
          New here?{" "}
          <Link to="/register" className="text-blue-700 hover:text-blue-800 font-semibold" data-testid="go-register-link">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
